import { isSameDay } from 'date-fns';
import { ReactElement, useEffect, useState } from 'react';
import {
  formatInputDate,
  isWithinDateBounds,
  parseTypedDate,
  resolveTypedDate,
} from '../../../helpers/DateInputHelper';

type DateTextInputProps = {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  onEntryError: (message: string | undefined) => void;
  minDate: Date;
  maxDate: Date;
  placeholder: string;
  ariaLabel?: string;
  id?: string;
  isActive?: boolean;
  onClick?: () => void;
  onFocus?: () => void;
};

/*
  A keyboard-editable input for a single date.

  While the input has focus, the typed text is kept as a draft that is never overwritten by the
  committed value, so partially typed dates are not cleared mid-entry. Complete dates with a
  four-digit year are committed as they are typed. Everything else is resolved on blur: two-digit
  years are expanded, and invalid or out-of-bounds text is kept as rejected text and reports an
  error, leaving the committed date unchanged.

  Without focus, the input displays rejected text if there is any, otherwise the committed date. A
  date set elsewhere (calendar clicks, presets) replaces rejected text and clears its error.
  Resetting to an unchanged value, such as clearing an already empty date, does not reach the
  input, so parents remount it to discard rejected text.
*/
const DateTextInput = (props: DateTextInputProps): ReactElement => {
  const {
    value,
    onChange,
    onEntryError,
    minDate,
    maxDate,
    placeholder,
    ariaLabel,
    id,
    isActive,
    onClick,
    onFocus,
  } = props;

  const [draft, setDraft] = useState<string>();
  // Kept on screen after a failed entry so that the input never shows a date other than the one
  // the user typed while its error is reported.
  const [rejectedText, setRejectedText] = useState<string>();
  const committedText = value ? formatInputDate(value) : '';

  useEffect(() => {
    if (rejectedText === undefined) return;
    setRejectedText(undefined);
    onEntryError(undefined);
  }, [value]);

  const commit = (date: Date | undefined): void => {
    const isUnchanged = date && value ? isSameDay(date, value) : date === value;
    if (!isUnchanged) onChange(date);
  };

  const handleChange = (text: string): void => {
    setDraft(text);
    onEntryError(undefined);
    if (!text.trim()) {
      commit(undefined);
      return;
    }
    // Two-digit years are resolved on blur so that "1/5/20" is not committed while the user is
    // still typing "1/5/2024".
    const date = parseTypedDate(text, { allowTwoDigitYear: false });
    if (date && isWithinDateBounds(date, minDate, maxDate)) commit(date);
  };

  const handleBlur = (): void => {
    if (draft === undefined) return;
    setDraft(undefined);
    const { date, error } = resolveTypedDate(draft, minDate, maxDate);
    if (error) {
      setRejectedText(draft);
      onEntryError(error);
      return;
    }
    setRejectedText(undefined);
    commit(date);
  };

  return (
    <input
      aria-label={ariaLabel}
      className={isActive ? 'date-input active' : 'date-input'}
      id={id}
      onBlur={handleBlur}
      onChange={(e) => handleChange(e.target.value)}
      onClick={onClick}
      onFocus={() => {
        setDraft(rejectedText ?? committedText);
        if (onFocus) onFocus();
      }}
      placeholder={placeholder}
      type="text"
      value={draft ?? rejectedText ?? committedText}
    />
  );
};

export default DateTextInput;
